import React, { useEffect, useState, useRef, useContext } from "react";
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { UserContext } from '../../../context/UserContext'
import ErrorMessage from "../../../common/ErrorMessage/ErrorMessage"
import { modalStyles } from './styles'
import { DateField } from "@mui/x-date-pickers";
import { BASE_URL } from "../../../baseUrl"

const StrategyViewDialog = ({open, handleClose, id_client, id_strategy, size}) => {

    /*Common variables for all the dialogs*/ 
    const [login] = useContext(UserContext);
    const [errorMessage, setErrorMessage] = useState("");
    const [loading, setLoading] = useState(false);

    /*variables for canvas view and scatter*/
    const canvasRef = useRef(null);
    const [points, setPoints] = useState([]);

    const [desc_strategy,setDesc_Strategy] = useState("");
    const [x_axis, setX_Axis] = useState("");
    const [y_axis, setY_Axis] = useState("");
    const [validfrom, setValidFrom] = useState("");
    const [validto, setValidTo] = useState("");
    const [sdggroup, setSdgGroup] = useState("");
    

    const drawCanvas = () => {
        const canvas = canvasRef.current;
        if (!canvas) return; // Safety check to ensure canvas is not null
        const ctx = canvas.getContext('2d');
        // Draw axes and points every time the canvas needs to be updated
        drawAxes(ctx);
        points.forEach(point => {
          drawPoint(ctx, point.x, point.y, point.goaltext, point.validfrom, point.validto, point.sdg);
        });
        // Draw the current point from x_axis and y_axis state
        if (x_axis && y_axis) {
          drawPoint(ctx, parseFloat(x_axis), parseFloat(y_axis), desc_strategy);
        }
      };

    const drawAxes = (ctx) => {
        const canvas = canvasRef.current;
        ctx.clearRect(0,0,canvas.width, canvas.height);
        ctx.beginPath();
        ctx.strokeStyle = 'rgb(146, 209, 223)';
        ctx.moveTo(canvas.width / 2, 0);
        ctx.lineTo(canvas.width / 2, canvas.height);
        ctx.moveTo(0, canvas.height / 2);
        ctx.lineTo(canvas.width, canvas.height / 2);
        ctx.stroke();
        ctx.fillStyle = 'rgb(146, 209, 223)';
        ctx.fillText('Negative impact', 10, canvas.height / 2 + 12);
        ctx.rotate(Math.PI / -2);
        ctx.fillText('Less Common', -canvas.width + 10, canvas.width / 2 - 8);
        ctx.rotate(Math.PI / 2);
        ctx.fillText('Positive impact', canvas.width - 75, canvas.height / 2 - 8);
        ctx.rotate(Math.PI / -2);
        ctx.fillText(
          'More Common',
          -canvas.width + canvas.width - 75,
          canvas.width / 2 + 12
        );
        ctx.rotate(Math.PI / 2);
    };

    const drawPoint = (ctx, x, y, goaltext, validfrom = '', validto = '', sdg ='') => {
        const canvas = canvasRef.current;
        ctx.fillStyle = 'rgb(146, 209, 223)';
        ctx.beginPath();
        ctx.arc(
          x + canvas.width / 2,
          canvas.height / 2 - y,
          5,
          0,
          2 * Math.PI
        );
        ctx.fill();
        ctx.fillText(`${goaltext}`, x + canvas.width / 2 + 10, canvas.height / 2 - y);
        /*just in case to display more than param: `${goaltext} - ${sdg}`*/
    };

    useEffect(() => {
        if (open) {
          fetchStrategyData();
        }
      }, [open, id_client, id_strategy]);
    
    const fetchStrategyData = async () => {
        const requestOptions = {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + login.token,
          },
        };
        try {
          const response = await fetch(BASE_URL + `/api/strategy/${id_client}/${id_strategy}`, requestOptions);
          if (!response.ok) {
            throw new Error('Could not get the strategy info');
          }
          const data = await response.json();
          setDesc_Strategy(data.Desc_Strategy);
          setX_Axis(data.X_Axis);
          setY_Axis(data.Y_Axis);
          setValidFrom(data.ValidFrom)
          setValidTo(data.ValidTo)
          setSdgGroup(data.SDG_Group)
           // Set other state as needed
        } catch (error) {
          setErrorMessage(error.message);
        }
    };

    useEffect(() => {
        if (open && canvasRef.current) {
            drawCanvas();
        }
      }, [points, x_axis, y_axis, open]); // Redraw canvas when these dependencies change


    return (
        <Dialog
            maxWidth={size} fullWidth
            open={open}
            onClose={(event, reason) => {
                if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
                    handleClose();
                }
            }}
            ModalProps={{disableEnforceFocus: true}}
            classes={{ paper: 'custom-dialog' }}
            PaperProps={{ style: { backgroundColor: '#231E39'}}}
        >
            <DialogTitle style={{color: 'white'}}>Strategy details</DialogTitle>
            <DialogContent style={{color: 'white'}}>
                <DialogContentText style={{color: 'whitesmoke'}}>
                    {desc_strategy}
                </DialogContentText>

                <TextField 
                    autoFocus
                    margin="dense"
                    id="x_axis"
                    label="X coordinate"
                    value = {x_axis}
                    type="text"
                    fullWidth
                    variant="standard"
                    InputProps={{
                        readOnly: true,
                    }}
                    sx={{
                        '& .MuiInput-input': { backgroundColor: '#25274D', color: 'lightgray' },
                        '& .MuiInputLabel-root':  { color: 'gray'}                 
                    }}
                    
                />

                <TextField
                    autoFocus
                    margin="dense"
                    id="y_axis"
                    label="Y coordinate"
                    value = {y_axis}
                    type="text"
                    fullWidth
                    variant="standard"
                    InputProps={{
                        readOnly: true,
                    }}
                    sx={{
                        '& .MuiInput-input': { backgroundColor: '#25274D', color: 'lightgray' },
                        '& .MuiInputLabel-root':  { color: 'gray'}                 
                    }}
                />

                <TextField
                    margin="dense"
                    id="validfrom"
                    label="Valid from"
                    value={validfrom} // Make sure this is in 'YYYY-MM-DD' format
                    type="date"
                    fullWidth
                    variant="standard"
                    InputProps={{
                        readOnly: true,
                    }}
                    sx={{
                        '& .MuiInput-input': { backgroundColor: '#25274D', color: 'lightgray' },
                        '& .MuiInputLabel-root':  { color: 'gray'}                 
                    }}
                />

                <TextField
                    margin="dense"
                    id="validto"
                    label="Valid to"
                    value={validto} // Make sure this is in 'YYYY-MM-DD' format
                    type="date"
                    fullWidth
                    variant="standard"
                    InputProps={{
                        readOnly: true,
                    }}
                    sx={{
                        '& .MuiInput-input': { backgroundColor: '#25274D', color: 'lightgray' },
                        '& .MuiInputLabel-root':  { color: 'gray'}                 
                    }}
                />

                <TextField
                    autoFocus
                    margin="dense"
                    id="sdggroup"
                    label="SDG Group"
                    value = {sdggroup}
                    type="text"
                    fullWidth
                    variant="standard"
                    InputProps={{
                        readOnly: true,
                    }}
                    sx={{
                        '& .MuiInput-input': { backgroundColor: '#25274D', color: 'lightgray' },
                        '& .MuiInputLabel-root':  { color: 'gray'}                 
                    }}
                />

                <section className={modalStyles.container} id="about">

                    {/* Plotting the Canvas for the Scatterplot Edit */}
                        <div className={modalStyles.content}>
                            <canvas
                                id="scatter"
                                className="scatter"
                                ref={canvasRef}
                                width={500}
                                height={500}
                            ></canvas>
                        </div>
                </section> 

            </DialogContent>
            <DialogActions
            sx={{'& .MuiButton-text' : {color: 'var(--main-color)'}}}
            >
                <Button  onClick={handleClose}
                          sx={{
                            paddingRight: '1.5rem',
                            marginRight: '1.5rem',
                            border: '1px solid var(--main-color)',
                            
                          }}
                >Close View</Button>
            </DialogActions>
        </Dialog>  
    )

}

export default StrategyViewDialog;